import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useArchetype } from '../../contexts/ArchetypeContext';
import { useAdmin } from '../../contexts/AdminContext';
import useUserRole from '../../hooks/useUserRole';
import useAuthStore from '../../store/authStore';
import { get } from '../../helpers/apiHelper';
import AdminPanel from '../../pages/admin/AdminPanel';
import Dashboard from '../../pages/common/Dashboard';
import DashboardCustom from '../../templates/empanadaclub/Dashboard';
import { shouldUseCustomTemplate } from '../../utils/customTemplateHelper';
import { FaSpinner } from 'react-icons/fa';

const DashboardRedirect = () => {
  const { hasArchetype, loading: archetypeLoading } = useArchetype();
  const { isAdminMode } = useAdmin();
  const { userRole, roleLoading } = useUserRole();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(true);
  const [useCustomTemplate, setUseCustomTemplate] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.userId || !hasArchetype) return;

      try {
        // Verificar todas las configuraciones necesarias
        const [marcasResponse, buyerPersonaResponse, storesResponse] = await Promise.all([
          get('marcas').catch(() => ({ length: 0 })),
          get('buyer-persona/result').catch(() => null),
          get('tiendas').catch(() => ({ length: 0 }))
        ]);

        // Verificar si debe usar template personalizado
        const shouldUse = shouldUseCustomTemplate(user.userId, marcasResponse);
        setUseCustomTemplate(shouldUse);

        // Necesita onboarding si falta alguna configuración
        const needs = !marcasResponse.length ||
          !buyerPersonaResponse ||
          !storesResponse.length;

        setNeedsOnboarding(needs);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // En caso de error, asumimos que necesita onboarding
        setNeedsOnboarding(true);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user, hasArchetype]);

  // Si está cargando, mostrar spinner
  if (loading || roleLoading || archetypeLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Si es admin, mostrar panel admin o dashboard según adminMode
  if (userRole === 'ADMIN_ROLE') {
    return isAdminMode ? <AdminPanel /> : (useCustomTemplate ? <DashboardCustom /> : <Dashboard />);
  }

  // Si no tiene arquetipo, mostrar pantalla de espera
  if (!hasArchetype) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Configurando tu perfil...</p>
        </div>
      </div>
    );
  }

  // Si tiene arquetipo pero necesita onboarding, redirigir a onboarding
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Si tiene arquetipo y completó onboarding, mostrar dashboard
  return useCustomTemplate ? <DashboardCustom /> : <Dashboard />;
};

export default DashboardRedirect;
